export interface AdminNotificationData {
  nombre: string;
  email: string;
  fecha: string;
  [key: string]: string | number | boolean | undefined;
}

export type ActionType =
  | 'Contacto General'
  | 'Nuevo Candidato Registrado'
  | 'Nueva Empresa Registrada'
  | 'Solicitud de Reunión Estratégica'
  | 'Interés Biblioteca Estratégica'
  | 'Solicitud de Acceso Biblioteca';

const ADMIN_EMAIL = 'info@bnagenzia.com';
const SENDER_NAME = 'BN ĀGENZIA';
const MAX_RETRIES = 3;
const RETRY_DELAY = 2000;

// Plantilla HTML para notificaciones al admin
const getAdminEmailTemplate = (type: ActionType, data: AdminNotificationData): string => {
  const fields = Object.entries(data)
    .filter(([key]) => key !== 'fecha')
    .map(([key, value]) => {
      const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
      return `
        <tr>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; font-weight: 600; color: #374151; width: 180px;">
            ${label}:
          </td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; color: #1f2937;">
            ${value || 'No especificado'}
          </td>
        </tr>
      `;
    })
    .join('');

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f9fafb;">
      <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f9fafb; padding: 40px 20px;">
        <tr>
          <td align="center">
            <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);">
              <!-- Header -->
              <tr>
                <td style="background: linear-gradient(135deg, #000000 0%, #1a1a1a 100%); padding: 32px; text-align: center;">
                  <h1 style="margin: 0; color: #ffffff; font-size: 24px; font-weight: 700; letter-spacing: 0.5px;">
                    ${SENDER_NAME}
                  </h1>
                  <p style="margin: 8px 0 0 0; color: #d1d5db; font-size: 14px;">
                    Sistema de Notificaciones
                  </p>
                </td>
              </tr>
              
              <!-- Content -->
              <tr>
                <td style="padding: 32px;">
                  <div style="background-color: #f3f4f6; border-left: 4px solid #000000; padding: 16px 20px; margin-bottom: 24px; border-radius: 4px;">
                    <h2 style="margin: 0; color: #111827; font-size: 18px; font-weight: 600;">
                      ${type}
                    </h2>
                    <p style="margin: 4px 0 0 0; color: #6b7280; font-size: 14px;">
                      ${data.fecha}
                    </p>
                  </div>
                  
                  <table width="100%" cellpadding="0" cellspacing="0" style="border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden;">
                    ${fields}
                  </table>
                </td>
              </tr>
              
              <!-- Footer -->
              <tr>
                <td style="background-color: #f9fafb; padding: 24px; text-align: center; border-top: 1px solid #e5e7eb;">
                  <p style="margin: 0; color: #6b7280; font-size: 13px;">
                    Esta notificación fue generada automáticamente por el sistema de BN ĀGENZIA
                  </p>
                  <p style="margin: 8px 0 0 0; color: #9ca3af; font-size: 12px;">
                    ${ADMIN_EMAIL}
                  </p>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </body>
    </html>
  `;
};

// Plantilla HTML para confirmación al usuario
const getUserConfirmationTemplate = (userName: string, actionType: ActionType): string => {
  const messages: Record<ActionType, { title: string; message: string }> = {
    'Contacto General': {
      title: '¡Gracias por contactarnos!',
      message: 'Hemos recibido tu mensaje correctamente. Nuestro equipo lo revisará y te responderá en un plazo máximo de 48 horas.',
    },
    'Nuevo Candidato Registrado': {
      title: '¡Bienvenido a BN ĀGENZIA!',
      message: 'Tu registro como candidato se ha completado exitosamente. Ahora formas parte de nuestra red de profesionales internacionales. Pronto recibirás información sobre oportunidades que se ajusten a tu perfil.',
    },
    'Nueva Empresa Registrada': {
      title: '¡Bienvenidos a BN ĀGENZIA!',
      message: 'El registro de su empresa se ha completado exitosamente. Nuestro equipo de consultoría estratégica se pondrá en contacto con ustedes en breve para comenzar a trabajar en sus necesidades de talento internacional.',
    },
    'Solicitud de Reunión Estratégica': {
      title: 'Solicitud recibida',
      message: 'Hemos recibido su solicitud de reunión estratégica. Nuestro equipo de consultores analizará sus requerimientos y se pondrá en contacto con ustedes en un plazo máximo de 48 horas para coordinar la sesión.',
    },
    'Interés Biblioteca Estratégica': {
      title: 'Te avisaremos pronto',
      message: 'Gracias por tu interés en nuestra Biblioteca Estratégica Internacional. Te notificaremos por email tan pronto como esté disponible el acceso.',
    },
    'Solicitud de Acceso Biblioteca': {
      title: 'Solicitud de acceso recibida',
      message: 'Hemos recibido tu solicitud de acceso a la Biblioteca Estratégica Internacional. Nuestro equipo se pondrá en contacto contigo en breve para completar el proceso de activación.',
    },
  };

  const { title, message } = messages[actionType];

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f9fafb;">
      <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f9fafb; padding: 40px 20px;">
        <tr>
          <td align="center">
            <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);">
              <!-- Header -->
              <tr>
                <td style="background: linear-gradient(135deg, #000000 0%, #1a1a1a 100%); padding: 40px; text-align: center;">
                  <h1 style="margin: 0; color: #ffffff; font-size: 28px; font-weight: 700; letter-spacing: 0.5px;">
                    ${SENDER_NAME}
                  </h1>
                </td>
              </tr>
              
              <!-- Content -->
              <tr>
                <td style="padding: 40px;">
                  <h2 style="margin: 0 0 16px 0; color: #111827; font-size: 22px; font-weight: 600;">
                    Hola ${userName},
                  </h2>
                  
                  <div style="background-color: #f3f4f6; padding: 24px; border-radius: 8px; margin-bottom: 24px;">
                    <h3 style="margin: 0 0 12px 0; color: #000000; font-size: 18px; font-weight: 600;">
                      ${title}
                    </h3>
                    <p style="margin: 0; color: #374151; font-size: 15px; line-height: 1.6;">
                      ${message}
                    </p>
                  </div>
                  
                  <p style="margin: 0; color: #6b7280; font-size: 14px; line-height: 1.6;">
                    Si tienes alguna pregunta, no dudes en responder a este email o contactarnos directamente.
                  </p>
                </td>
              </tr>
              
              <!-- Footer -->
              <tr>
                <td style="background-color: #f9fafb; padding: 32px; text-align: center; border-top: 1px solid #e5e7eb;">
                  <p style="margin: 0 0 8px 0; color: #111827; font-size: 14px; font-weight: 600;">
                    ${SENDER_NAME}
                  </p>
                  <p style="margin: 0; color: #6b7280; font-size: 13px;">
                    Consultoría Estratégica Internacional
                  </p>
                  <p style="margin: 12px 0 0 0; color: #9ca3af; font-size: 12px;">
                    ${ADMIN_EMAIL}
                  </p>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </body>
    </html>
  `;
};

// Función auxiliar para esperar entre reintentos
const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

// Función para enviar email con reintentos
const sendEmailWithRetry = async (
  to: string,
  subject: string,
  html: string,
  retries = MAX_RETRIES
): Promise<boolean> => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      // Aquí se implementaría la llamada real al servicio de email
      // Por ahora, simulamos el envío y lo registramos en consola
      console.log(`[Email Service] Intento ${attempt}/${retries}`);
      console.log(`Para: ${to}`);
      console.log(`Asunto: ${subject}`);
      console.log(`HTML: ${html.substring(0, 100)}...`);

      // Simulación de envío exitoso
      // En producción, aquí iría la llamada a un servicio como SendGrid, AWS SES, etc.
      await delay(500);

      console.log(`[Email Service] ✓ Email enviado exitosamente a ${to}`);
      return true;
    } catch (error) {
      console.error(`[Email Service] ✗ Error en intento ${attempt}:`, error);

      if (attempt < retries) {
        console.log(`[Email Service] Reintentando en ${RETRY_DELAY}ms...`);
        await delay(RETRY_DELAY);
      } else {
        console.error(`[Email Service] ✗ Falló después de ${retries} intentos`);
        return false;
      }
    }
  }

  return false;
};

/**
 * Envía una notificación al email administrativo (info@bnagenzia.com)
 * con los datos de la acción realizada por el usuario
 */
export const sendAdminNotification = async (
  type: ActionType,
  data: AdminNotificationData
): Promise<boolean> => {
  const subject = `[${type}] - ${data.nombre}`;
  const html = getAdminEmailTemplate(type, data);

  console.log(`[Email Service] Enviando notificación admin: ${type}`);
  return await sendEmailWithRetry(ADMIN_EMAIL, subject, html);
};

/**
 * Envía un email de confirmación al usuario que completó una acción
 */
export const sendUserConfirmation = async (
  userEmail: string,
  userName: string,
  actionType: ActionType
): Promise<boolean> => {
  const subject = `Confirmación - ${SENDER_NAME}`;
  const html = getUserConfirmationTemplate(userName, actionType);

  console.log(`[Email Service] Enviando confirmación a usuario: ${userEmail}`);
  return await sendEmailWithRetry(userEmail, subject, html);
};

/**
 * Envía tanto la notificación al admin como la confirmación al usuario
 * Garantiza que la notificación al admin se envíe incluso si falla la confirmación al usuario
 */
export const sendNotifications = async (
  type: ActionType,
  data: AdminNotificationData,
  userEmail: string,
  userName: string
): Promise<{ adminSent: boolean; userSent: boolean }> => {
  console.log(`[Email Service] Iniciando envío de notificaciones para: ${type}`);

  // Enviar notificación al admin (prioritario)
  const adminSent = await sendAdminNotification(type, data);

  // Enviar confirmación al usuario (secundario)
  const userSent = await sendUserConfirmation(userEmail, userName, type);

  console.log(`[Email Service] Resultado - Admin: ${adminSent ? '✓' : '✗'}, Usuario: ${userSent ? '✓' : '✗'}`);

  return { adminSent, userSent };
};